import { Radio, RadioGroup, Space, Text } from '@billy/design-system'
import round from 'lodash/round'
import { ChangeEvent, ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { Difference, DifferenceType } from '@modules-deprecated/bankReconciliation/types'

import { BillType } from '../../../bills/enums/billType'
import {
  useContactBalance,
  useMultiplePayableSubjects,
  usePaymentModal,
} from '../../../paymentReconciliationModal/contexts'
import { getTypeFromPayableSubject } from '../../../paymentReconciliationModal/utils'
import { differenceTypeTKeys } from './constants/differenceTypeTKeys'
import * as Styled from './styles'

const ROUNDING_PRECISION_FOR_DIFFERENCE = 2

interface DifferenceSelectorProps {
  difference: Difference
}

export const DifferenceSelector = ({ difference }: DifferenceSelectorProps): ReactElement => {
  const { t } = useTranslation()
  const { subjectCurrencyId, selectedDifferenceType, setSelectedDifferenceType } = usePaymentModal()
  const { initialSubject, isCreditNoteSelected } = useMultiplePayableSubjects()
  const { contact } = useContactBalance()

  const isDifferencePositive = (difference?.differenceAmount || 0) > 0
  const formattedDifferenceAmount = Math.abs(
    round(difference?.differenceAmount || 0, ROUNDING_PRECISION_FOR_DIFFERENCE),
  )

  const radioGroupKey = `difference-${JSON.stringify(difference.types)}`

  const subKeyBasedOnSubjectType = useMemo(() => {
    const subjectType = getTypeFromPayableSubject(initialSubject)
    const isInvoiceType = subjectType === InvoiceType.Invoice && !isCreditNoteSelected
    const isBillCreditNote = subjectType === BillType.Bill && isCreditNoteSelected

    if (isInvoiceType || isBillCreditNote) {
      return InvoiceType.Invoice
    }
    return BillType.Bill
  }, [isCreditNoteSelected, initialSubject])

  const differenceHeaderTKey = useMemo(() => {
    const root = `${subKeyBasedOnSubjectType}.payment_reconcile.difference.`
    let key = root
    if (!isDifferencePositive) {
      key += 'positive.'
    } else {
      key += 'negative.'
    }

    if (!selectedDifferenceType) {
      key += 'not_selected'
      return key
    }

    switch (selectedDifferenceType) {
      case 'bankFee':
        key = `${root}bankFee.info`
        break
      case 'overpayment':
        key += 'overpayment'
        break
      case 'underpayment':
        key += 'underpayment'
        break
      default:
        break
    }

    return key
  }, [isDifferencePositive, selectedDifferenceType, subKeyBasedOnSubjectType])

  const handleDifferenceTypeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as DifferenceType
      setSelectedDifferenceType(value)
    },
    [setSelectedDifferenceType],
  )

  useEffect(() => {
    setSelectedDifferenceType(undefined)
  }, [difference, setSelectedDifferenceType])

  return (
    <Styled.DifferenceSelectorWrapper>
      <Text>
        {t(differenceHeaderTKey, {
          difference: formattedDifferenceAmount,
          currency: subjectCurrencyId,
          contactName: contact?.name,
        })}
      </Text>
      <Space size="s12" />
      <RadioGroup key={radioGroupKey} onChange={handleDifferenceTypeChange}>
        {difference.types.map((type) => {
          return (
            <Radio value={type} name={type} key={type} checked={type === selectedDifferenceType}>
              {t(differenceTypeTKeys[type])}
            </Radio>
          )
        })}
      </RadioGroup>
    </Styled.DifferenceSelectorWrapper>
  )
}
